import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { endpoints } from "../../api/endpoints";
import type { Page } from "../../types/user";
import type { CityOption, MetaBundle, StateOption, TargetOptions } from "./targeting";

export type SurveyStatus = "PENDING" | "PUBLISHED" | "COMPLETED" | "CANCELLED";

export interface Survey {
  id: number;
  surveyName: string;
  description: string | null;
  status: SurveyStatus;
  createdBy: string | null;
  startDate: string | null;
  endDate: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface SurveyQuery {
  pageNumber: number;
  pageSize: number;
  sort: "ASC" | "DESC";
  sortOn: string;
  search: string;
  status?: string;
}

export const DEFAULT_SURVEY_QUERY: SurveyQuery = {
  pageNumber: 0,
  pageSize: 25,
  sort: "DESC",
  sortOn: "id",
  search: "",
};

export function useSurveys(query: SurveyQuery) {
  return useQuery({
    queryKey: ["surveys", query],
    queryFn: async () => {
      const { data } = await api.get<Page<Survey>>(endpoints.surveys.list(), { params: query });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export function useSurvey(surveyId: number | null) {
  return useQuery({
    queryKey: ["survey", surveyId],
    queryFn: async () => {
      const { data } = await api.get<Survey>(endpoints.surveys.byId(String(surveyId)));
      return data;
    },
    enabled: surveyId !== null,
  });
}

function useInvalidate() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: ["surveys"] });
}

export function useCreateSurvey() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (payload: { surveyName: string; description: string }) => {
      const { data } = await api.post<Survey>(endpoints.surveys.create(), payload);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateSurvey() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({
      surveyId,
      payload,
    }: {
      surveyId: number;
      payload: { surveyName: string; description: string };
    }) => {
      const { data } = await api.put<Survey>(endpoints.surveys.update(String(surveyId)), payload);
      return data;
    },
    onSuccess: invalidate,
  });
}

/** publish, cancel, remind and copy all act on one survey and take no body. */
function useSurveyAction(url: (surveyId: string) => string, method: "post" | "put" | "get") {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (surveyId: number) => {
      const target = url(String(surveyId));
      const { data } =
        method === "get"
          ? await api.get(target)
          : method === "post"
            ? await api.post(target, undefined)
            : await api.put(target, undefined);
      return data;
    },
    onSuccess: invalidate,
  });
}

export const usePublishSurvey = () => useSurveyAction(endpoints.surveys.publish, "post");
export const useCancelSurvey = () => useSurveyAction(endpoints.surveys.cancel, "put");
export const useRemindSurvey = () => useSurveyAction(endpoints.surveys.remind, "post");
export const useCopySurvey = () => useSurveyAction(endpoints.surveys.copy, "post");

export function useDeleteSurvey() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (surveyId: number) => {
      const { data } = await api.delete(endpoints.surveys.remove(String(surveyId)));
      return data;
    },
    onSuccess: invalidate,
  });
}

export async function downloadSurveyResponses(surveyId: number): Promise<Blob> {
  const { data } = await api.get<Blob>(endpoints.surveys.download(String(surveyId)), {
    responseType: "blob",
  });
  return data;
}

export interface QuestionType {
  id: number;
  type: string;
  name: string;
}

export interface QuestionOption {
  description: string | null;
  id: number | null;
  option: string;
  optionType: string | null;
  questionId: number | null;
}

export interface SurveyQuestion {
  id?: number;
  question: string;
  /** The service wants the whole type object here, not just its id. */
  questionType: { id: number; type: string };
  questionTypeId?: number;
  displayOrder: number;
  surveyId?: number;
  options?: QuestionOption[];
}

export interface SurveyDetail extends Survey {
  questions: SurveyQuestion[];
}

export function useQuestionTypes() {
  return useQuery({
    queryKey: ["question-types"],
    queryFn: async () => {
      const { data } = await api.get<QuestionType[]>(endpoints.questionTypes.list());
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useSurveyDetail(surveyId: number | null) {
  return useQuery({
    queryKey: ["survey-detail", surveyId],
    queryFn: async () => {
      const { data } = await api.get<SurveyDetail>(endpoints.surveys.byId(String(surveyId)));
      return data;
    },
    enabled: surveyId !== null,
  });
}

function useInvalidateDetail(surveyId: number) {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: ["survey-detail", surveyId] });
}

/** The whole question set is posted at once, with displayOrder assigned by position. */
export function useSaveQuestions(surveyId: number) {
  const invalidate = useInvalidateDetail(surveyId);
  return useMutation({
    mutationFn: async (questions: SurveyQuestion[]) => {
      const { data } = await api.post(endpoints.surveys.questions(String(surveyId)), {
        questions: questions.map((question, index) => ({ ...question, displayOrder: index + 1 })),
      });
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteQuestion(surveyId: number) {
  const invalidate = useInvalidateDetail(surveyId);
  return useMutation({
    mutationFn: async (questionId: number) => {
      const { data } = await api.delete(
        endpoints.surveys.question(String(surveyId), String(questionId)),
      );
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useReorderQuestions(surveyId: number) {
  const invalidate = useInvalidateDetail(surveyId);
  return useMutation({
    mutationFn: async (questions: SurveyQuestion[]) => {
      const { data } = await api.post(endpoints.surveys.questionOrder(String(surveyId)), {
        questions: questions.map((question, index) => ({ ...question, displayOrder: index + 1 })),
      });
      return data;
    },
    onSuccess: invalidate,
  });
}

// --- Targeting -------------------------------------------------------------

export function useTargetOptions(surveyId: number | null) {
  return useQuery({
    queryKey: ["survey-target", surveyId],
    queryFn: async () => {
      const { data } = await api.get<TargetOptions>(
        endpoints.surveys.targetOptions(String(surveyId)),
      );
      return data;
    },
    enabled: surveyId !== null,
  });
}

export function useTargetMetaOptions() {
  return useQuery({
    queryKey: ["target-meta-options"],
    queryFn: async () => {
      const { data } = await api.get<MetaBundle>(endpoints.metas.allOptions());
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useStates() {
  return useQuery({
    queryKey: ["target-states"],
    queryFn: async () => {
      const { data } = await api.get<StateOption[]>(endpoints.metas.states());
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCities() {
  return useQuery({
    queryKey: ["target-cities"],
    queryFn: async () => {
      const { data } = await api.get<CityOption[]>(endpoints.metas.cities());
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveTargetOptions(surveyId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: TargetOptions) => {
      const { data } = await api.post(endpoints.surveys.targetOptions(String(surveyId)), payload);
      return data;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["survey-target", surveyId] });
      client.invalidateQueries({ queryKey: ["surveys"] });
    },
  });
}
